import L from 'leaflet';
import { FullScreen } from 'leaflet.fullscreen';
import { maptilerLayer } from '@maptiler/leaflet-maptilersdk';

type EtablissementData = {
    id: number;
    lat: number;
    lng: number;
    group: string;
    url: string | false;
    title: string;
    types: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
    icon: string;
    iconHover: string;
};

type MapStyleLayer = {
    id: string;
    layout?: {
        visibility?: string;
        [key: string]: unknown;
    };
    'source-layer'?: string;
    [key: string]: unknown;
};

type MapStyleDefinition = Record<string, unknown> & {
    layers: MapStyleLayer[];
};


function shouldHideMapLabelLayer(layer: MapStyleLayer): boolean {
    const id = layer.id.toLowerCase();
    const sourceLayer = (layer['source-layer'] ?? '').toLowerCase();

    const isRoadLabel =
        (id.includes('road') || sourceLayer.includes('transportation_name') || sourceLayer.includes('road')) &&
        (id.includes('label') || id.includes('name') || sourceLayer.includes('name'));

    const isRoadShield =
        id.includes('shield') ||
        (id.includes('motorway') && id.includes('label')) ||
        sourceLayer.includes('transportation_name');

    const isPoi =
        id.includes('poi') ||
        id.includes('airport') ||
        id.includes('aerodrome') ||
        id.includes('hotel') ||
        sourceLayer.includes('poi') ||
        sourceLayer.includes('aerodrome_label');

    return isRoadLabel || isRoadShield || isPoi;
}


function isPlaceLabelLayer(layer: MapStyleLayer): boolean {
    const id = layer.id.toLowerCase();
    const sourceLayer = (layer['source-layer'] ?? '').toLowerCase();
    return sourceLayer.includes('place') || id.includes('place') || id.includes('city') || id.includes('town') || id.includes('village');
}

function buildFilteredMapStyle(style: MapStyleDefinition): MapStyleDefinition {
    return {
        ...style,
        layers: style.layers.map(layer => {
            if (shouldHideMapLabelLayer(layer)) {
                return {
                    ...layer,
                    layout: {
                        ...(layer.layout ?? {}),
                        visibility: 'none',
                    },
                };
            }

            if (isPlaceLabelLayer(layer)) {
                return {
                    ...layer,
                    paint: {
                        ...(layer.paint as Record<string, unknown> ?? {}),
                        'text-halo-color': '#ffffff',
                        'text-halo-width': 1,
                    },
                };
            }

            return layer;
        }),
    };
}

export default function initListeEtablissements(): void {
    const mapEl = document.querySelector<HTMLElement>('.js-etablissement-map');
    if (!mapEl) return;

    const rawData = mapEl.dataset.etablissements;
    if (!rawData) return;

    const etablissements: EtablissementData[] = JSON.parse(rawData);
    const listEl = document.querySelector<HTMLElement>('.js-liste-etablissements');
    const filtersWrapper = document.querySelector<HTMLElement>('.js-wrapper-etablissement-filters');
    const resetBtn = filtersWrapper?.querySelector<HTMLButtonElement>('.js-reset-filters') ?? null;
    const cards = Array.from(document.querySelectorAll<HTMLElement>('.js-card-etablissement'));

    // Stockage des markers Leaflet indexés par post ID
    const markers = new Map<number, L.Marker>();
    const markerIcons = new Map<number, { normal: L.Icon; hover: L.Icon }>();

    // -------------------------
    // Initialisation de la carte Leaflet
    // -------------------------
    const map = L.map(mapEl, {
        center: [44.3, -0.8],
        zoom: 8,
        zoomControl: true,
    });

    map.addControl(new FullScreen({
        position: 'topleft',
        title: 'Voir en plein écran',
        titleCancel: 'Quitter le plein écran',
        forceSeparateButton: true,
    }));

    const mapTilerStyleUrl = 'https://api.maptiler.com/maps/019d8c7c-36c8-75ec-a369-b39a0eeff91a/style.json';
    const mapTilerApiKey = 'ZORgILLUK4MIwdQkyoVs';
    const osmFallbackLayer = () =>
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
            maxZoom: 19,
        }).addTo(map);

    if (mapTilerApiKey) {
        void fetch(`${mapTilerStyleUrl}?key=${encodeURIComponent(mapTilerApiKey)}`)
            .then(response => {
                if (!response.ok) {
                    throw new Error('MapTiler style fetch failed');
                }
                return response.json() as Promise<MapStyleDefinition>;
            })
            .then(style => {
                maptilerLayer({
                    apiKey: mapTilerApiKey,
                    style: buildFilteredMapStyle(style),
                }).addTo(map);
            })
            .catch(() => {
                osmFallbackLayer();
            });
    } else {
        // Fallback local si la couche vectorielle ne peut pas se charger.
        osmFallbackLayer();
    }

    etablissements.forEach(etab => {
        const iconNormal = L.icon({
            iconUrl: etab.icon,
            iconSize: [32, 42],
            iconAnchor: [16, 42],
        });
        const iconHover = L.icon({
            iconUrl: etab.iconHover,
            iconSize: [32, 42],
            iconAnchor: [16, 42],
        });

        const marker = L.marker([etab.lat, etab.lng], { icon: iconNormal }).addTo(map);

        marker.on('mouseover', () => marker.setIcon(iconHover));
        marker.on('mouseout', () => marker.setIcon(iconNormal));

        const svgBase = etab.icon.substring(0, etab.icon.lastIndexOf('/') + 1);

        let popupContent = `<div class="card-etablissement"><div class="card-body">`;
        popupContent += `<span class="card-title typography-body-m">${etab.title}</span>`;

        if (etab.types) {
            popupContent += `<div class="card-info card-type-etablissement">`;
            popupContent += `<span class="icon" aria-hidden="true"><img src="${svgBase}icon-home.svg" width="16" height="16" alt=""></span>`;
            popupContent += `<span>${etab.types}</span></div>`;
        }

        if (etab.address) {
            popupContent += `<div class="card-info card-address">`;
            popupContent += `<span class="icon" aria-hidden="true"><img src="${svgBase}icon-poi.svg" width="16" height="16" alt=""></span>`;
            popupContent += `<span>${etab.address}</span></div>`;
        }

        if (etab.phone) {
            popupContent += `<div class="card-info card-phone">`;
            popupContent += `<span class="icon" aria-hidden="true"><img src="${svgBase}icon-phone.svg" width="16" height="16" alt=""></span>`;
            popupContent += `<span>${etab.phone}</span></div>`;
        }

        if (etab.email) {
            popupContent += `<div class="card-info card-email">`;
            popupContent += `<span class="icon" aria-hidden="true"><img src="${svgBase}icon-mail.svg" width="16" height="16" alt=""></span>`;
            popupContent += `<span>${etab.email}</span></div>`;
        }

        popupContent += `</div>`;

        if (etab.url) {
            popupContent += `<a href="${etab.url}" class="card-arrow" aria-label="Voir l'établissement">`;
            popupContent += `<img src="${svgBase}arrow-right.svg" width="16" height="16" alt=""></a>`;
        }

        popupContent += `</div>`;

        marker.bindPopup(popupContent, { className: 'popup-etablissement', maxWidth: 357 });

        markers.set(etab.id, marker);
        markerIcons.set(etab.id, { normal: iconNormal, hover: iconHover });

        // Hover carte ↔ marker
        const card = document.querySelector<HTMLElement>(
            `.js-card-etablissement[data-post-id="${etab.id}"]`
        );
        if (card) {
            card.addEventListener('mouseenter', () => marker.setIcon(iconHover));
            card.addEventListener('mouseleave', () => marker.setIcon(iconNormal));
        }
    });

    if (listEl) {
        listEl.addEventListener('mouseover', event => {
            const card = (event.target as HTMLElement).closest<HTMLElement>('.js-card-etablissement');
            if (!card || !listEl.contains(card)) return;

            const postId = parseInt(card.dataset.postId ?? '0', 10);
            const marker = markers.get(postId);
            const icons = markerIcons.get(postId);
            if (marker && icons) {
                marker.setIcon(icons.hover);
            }
        });

        listEl.addEventListener('mouseout', event => {
            const card = (event.target as HTMLElement).closest<HTMLElement>('.js-card-etablissement');
            if (!card || !listEl.contains(card)) return;

            const relatedTarget = event.relatedTarget as Node | null;
            if (relatedTarget && card.contains(relatedTarget)) return;

            const postId = parseInt(card.dataset.postId ?? '0', 10);
            const marker = markers.get(postId);
            const icons = markerIcons.get(postId);
            if (marker && icons) {
                marker.setIcon(icons.normal);
            }
        });
    }

    if (!filtersWrapper) return;

    // -------------------------
    // Logique de filtrage client-side
    // -------------------------
    function getActiveFilters(): { territoire: number[]; type: number[] } {
        const territoireChecked = filtersWrapper!.querySelectorAll<HTMLInputElement>(
            'input[data-filter-type="territoire"]:checked'
        );
        const typeChecked = filtersWrapper!.querySelectorAll<HTMLInputElement>(
            'input[data-filter-type="type_etablissement"]:checked'
        );
        return {
            territoire: Array.from(territoireChecked).map(el => parseInt(el.value, 10)),
            type: Array.from(typeChecked).map(el => parseInt(el.value, 10)),
        };
    }

    function updateNbSelected(dropdown: HTMLElement): void {
        const count = dropdown.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').length;
        const nbEl = dropdown.querySelector<HTMLElement>('.nb-selected-checkbox');
        if (nbEl) {
            nbEl.textContent = count > 0 ? String(count) : '';
        }
    }

    function applyFilters(): void {
        const { territoire, type } = getActiveFilters();
        const hasActiveFilters = territoire.length > 0 || type.length > 0;

        resetBtn?.classList.toggle('visible', hasActiveFilters);

        cards.forEach(card => {
            const cardTerritoires = card.dataset.territoire
                ? card.dataset.territoire.split(',').map(Number).filter(Boolean)
                : [];
            const cardTypes = card.dataset.type
                ? card.dataset.type.split(',').map(Number).filter(Boolean)
                : [];

            const matchesTerritoire =
                territoire.length === 0 ||
                territoire.some(id => cardTerritoires.includes(id));

            const matchesType =
                type.length === 0 ||
                type.some(id => cardTypes.includes(id));

            const isVisible = matchesTerritoire && matchesType;
            card.classList.toggle('is-hidden', !isVisible);

            // Synchronisation des markers Leaflet
            const postId = parseInt(card.dataset.postId ?? '0', 10);
            const marker = markers.get(postId);
            if (marker) {
                if (isVisible) {
                    if (!map.hasLayer(marker)) marker.addTo(map);
                } else {
                    map.removeLayer(marker);
                }
            }
        });
    }

    // Boutons « Appliquer » dans les dropdowns
    filtersWrapper.querySelectorAll<HTMLButtonElement>('.js-apply-etablissement-filters').forEach(btn => {
        btn.addEventListener('click', () => {
            const dropdown = btn.closest<HTMLElement>('.js-dropdown-container');
            if (dropdown) updateNbSelected(dropdown);
            applyFilters();
            btn.closest<HTMLElement>('.js-dropdown-container')?.classList.remove('open');
        });
    });

    // Boutons « Effacer les filtres » locaux (js-reset-input-dropdown)
    filtersWrapper.querySelectorAll<HTMLButtonElement>('.js-reset-input-dropdown').forEach(btn => {
        btn.addEventListener('click', () => {
            const dropdown = btn.closest<HTMLElement>('.js-dropdown-container');
            dropdown?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(cb => {
                cb.checked = false;
            });
            if (dropdown) updateNbSelected(dropdown);
            applyFilters();
        });
    });

    // Bouton reset global (js-reset-filters)
    resetBtn?.addEventListener('click', () => {
        filtersWrapper.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(cb => {
            cb.checked = false;
        });
        filtersWrapper.querySelectorAll<HTMLElement>('.js-dropdown-container').forEach(dropdown => {
            updateNbSelected(dropdown);
        });
        applyFilters();
    });

    // Mise à jour du compteur au changement de checkbox
    filtersWrapper.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
            const dropdown = cb.closest<HTMLElement>('.js-dropdown-container');
            if (dropdown) updateNbSelected(dropdown);
        });
    });
}
